import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useRef, useState } from 'react';
import slugify from 'react-slugify';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import LanguageManagerGuard from '../../../../components/Language/LanguageManagerGuard';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useToastContext } from '../../../../contexts/ToastContext';
import { privateFetch } from '../../../../utils/apiHelper';
import { CONSTANT } from '../../../../utils/constants';

function NewsManager({ isOpen, onClose, news, loading, onUpdate, onCreate }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(undefined);
  const [imageLoading, setImageLoading] = useState(false);
  const { showToast } = useToastContext();
  const fileUploadRef = useRef(null);

  const { accountContext } = useAccountContext();

  useEffect(() => {
    if (news) {
      setTitle(news.title);

      setImage(news.image);
      setContent(news.content);
    } else {
      setTitle('');
      setImage(undefined);
      setContent('');
    }
  }, [news, isOpen]);

  const uploadImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setImage(res);
    });
    setImageLoading(false);
  };

  const onUpload = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClear();
    } else {
      setImageLoading(true);

      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('title', slugify(file.title));
      imageForm.append('file', file);
      uploadImage(imageForm);
    }
  };

  const onClear = () => {
    setImage(undefined);
    fileUploadRef.current.clear();
  };

  const handleValidate = () => {
    const newsPayload = {
      title,
      image: image?.id,
      content
    };
    news ? onUpdate(newsPayload) : onCreate(newsPayload);
  };

  const formIsValid = () => {
    return title && image && content;
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${news ? 'modification' : 'création'}`}
        className="w-full"
        onClick={handleValidate}
        severity="success"
        disabled={!formIsValid()}
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: 700 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={
        <h3 className="text-slate-800 text-2xl font-medium">{`${news ? 'Modifier' : 'Ajouter'} un article`}</h3>
      }>
      <LanguageManagerGuard isCreation={!news}>
        <div className="sidebar-content-container">
          <div className="form-wrapper !pb-60">
            <FormInput label={`Titre de l'article`} required isValid={title.length} translatable>
              <InputText
                id="title"
                value={title}
                maxLength={300}
                placeholder="Veuillez saisir le titre de l'article"
                onChange={(e) => setTitle(e.target.value)}
              />
            </FormInput>
            <FormInput label={'Contenu'} required isValid={content} translatable>
              <RichTextEditor
                value={content}
                onChange={(value) => {
                  setContent(value);
                }}
                acceptHeaders
                headerList={[3, 4, false]}
              />
            </FormInput>

            <FormInput key={'file'} label={`Photo principale (max. 10Mo)`} required isValid={image}>
              {image && (
                <img
                  src={image.file}
                  className="max-w-[400px] max-h-[400px] object-contain object-left mb-5"
                  alt="Image principale du programme"
                />
              )}
              <div className="flex flex-row gap-x-3">
                {image && (
                  <Button
                    key={'delete-img'}
                    label={"Supprimer l'image"}
                    outlined
                    onClick={onClear}
                    severity="danger"
                  />
                )}
                {imageLoading ? (
                  <div className="w-[50px] h-[50px] p-1">
                    <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                  </div>
                ) : (
                  <FileUpload
                    ref={fileUploadRef}
                    auto
                    disabled={image}
                    mode="basic"
                    title="file"
                    accept="image/*"
                    customUpload
                    uploadHandler={onUpload}
                    chooseLabel="Depuis mon ordinateur"
                  />
                )}
              </div>
            </FormInput>
          </div>
          {footerButtons}
        </div>
      </LanguageManagerGuard>
    </Sidebar>
  );
}

export default NewsManager;
